import React, { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, DropdownSelect, LogoLoader } from "../../../components";
import InfoDataCardReports from "./InfoDataCardReports";
import PerformanceReport from "./PerformanceReport";
import JobReports from "./JobReports";
import UpToDateOverview from "./UpToDateOverview";
import { useDispatch, useSelector } from "react-redux";
import { getBDMCompareMetricsStart, getBDMMetricsGroupStart, getBDMMetricsOverviewStart, getBDMMetricsStart, getBDMRoleBasedMetricsStart, getRACompareMetricsStart, getRAMetricsGroupStart, getRAMetricsOverviewStart, getRAMetricsStart, getRARoleBasedMetricsStart, getRecruiterCompareMetricsStart, getRecruiterMetricsGroupStart, getRecruiterMetricsOverviewStart, getRecruiterMetricsStart, getRecruiterRoleBasedMetricsStart, setreportToHierarchy } from "../../../redux/slices/atsDashboard/atsDashboardSlice";
import { getDashboardHierarchy } from "../../../redux/slices/atsDashboard/atsDashboardApi";
import { getEndOfDay, getStartOfDay } from "../../../utils/common";
import { combineByUserTimezone } from "../../../utils/dateUtils";
import { formatWords2 } from "../../../utils/stringUtils";

function getCompareDateRange(from, to) {
  if (!from || !to) return null;
  const start = new Date(from);
  const end = new Date(to);

  const dayDiff =
    Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

  const compareTo = new Date(start);
  compareTo.setMilliseconds(-1);

  const compareFrom = new Date(compareTo);
  compareFrom.setDate(compareFrom.getDate() - (dayDiff - 1));

  compareFrom.setHours(0, 0, 0, 0);
  compareTo.setHours(23, 59, 59, 999);

  return { from: getStartOfDay(compareFrom), to: getEndOfDay(compareTo) };
}

const OtherUsersDashboard = ({
  finalDataDuration,
  roleType
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading, metrics, roleBasedMetrics, metricsOverview, metricsGroup, reportToHierarchy, compareMetrics, compareTeamMetrics,
    teamMetrics, teamRoleBasedMetrics, teamMetricsOverview, teamMetricsGroup } = useSelector((state) => state.atsDashboard);

  const [hasSubordinates, setHasSubordinates] = useState(reportToHierarchy?.length > 1);
  const [activeButton, setActiveButton] = useState("myPerformance");
  const [performanceReportTeamNamesIds, setPerformanceReportTeamNamesIds] = useState([]);
  const [jobReportTeamNamesIds, setJobReportTeamNamesIds] = useState([]);
  const [performanceReportHirerchy, setPerformanceReportHierarchy] = useState(reportToHierarchy || []);
  const [stateLoading, setStateLoading] = useState(loading);

  const normalizedDateRange = useMemo(() => {
    if (!finalDataDuration?.from) return null;

    const from = new Date(finalDataDuration.from);
    const to = finalDataDuration.to
      ? new Date(finalDataDuration.to)
      : new Date();

    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);

    return { from, to };
  }, [finalDataDuration]);

  const compareDateRange = useMemo(() => {
    if (!finalDataDuration) return null;
    return getCompareDateRange(
      finalDataDuration.from,
      finalDataDuration.to
    );
  }, [finalDataDuration]);

  const fetchMetrics = () => {
    const type = activeButton === "myPerformance" ? false : true

    if (roleType === 2) {
      const fil = {addedBy: {
        type: "RA_METRICS",
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : reportToHierarchy?.map(u => u.uiid) || [], 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRAMetricsStart({fil, type, isCompare: false}));
    } else if (roleType === 3) {
      const fil = {addedBy: {
        type: "BDM_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : reportToHierarchy?.map(u => u.uiid) || [], 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to,
        queryMode: "BDM_METRICS"
      }}      
      dispatch(getBDMMetricsStart({fil, type, isCompare: false}));
    } else if (roleType === 4) {
      const fil = {addedBy: {
        type: "RECRUITER_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : reportToHierarchy?.map(u => u.uiid) || [], 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRecruiterMetricsStart({fil, type,queryMode: "RECRUITER_METRICS", isCompare: false}));
    }
  };

  const fetchCompareMetrics = () => {
    if (!compareDateRange) return;

    const type = activeButton === "myPerformance" ? false : true;

    const payload = {
      value:
        activeButton === "myPerformance"
          ? [user?.data?.uiid]
          : reportToHierarchy?.map((u) => u.uiid) || [],
      startDate: compareDateRange.from,
      endDate: compareDateRange.to
    };

    if (roleType === 2) {
      dispatch(
        getRACompareMetricsStart({ fil: { addedBy: { type: "RA_METRICS", ...payload } }, type, isCompare: true })
      );
    } else if (roleType === 3) {
      dispatch(
        getBDMCompareMetricsStart({ fil: { addedBy: { type: "BDM_METRICS", queryMode: "BDM_METRICS", ...payload } }, type, isCompare: true })
      );
    } else if (roleType === 4) {
      dispatch(
        getRecruiterCompareMetricsStart({ fil: { addedBy: { type: "RECRUITER_METRICS",queryMode: "RECRUITER_METRICS", ...payload } }, type, isCompare: true })
      );
    }
  };

  const fetchRoleBasedMetrics = () => {
    const type = activeButton === "myPerformance" ? false : true

    if (roleType === 2) {
      const fil = {addedBy: {
        type: "RA_JOBPOSTING_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : jobReportTeamNamesIds, 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRARoleBasedMetricsStart({fil, type}));
    } else if (roleType === 3) {
      const fil = {addedBy: {
        type: "BDM_SUBMISSIONS_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : jobReportTeamNamesIds, 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to,
        
      }}
      dispatch(getBDMRoleBasedMetricsStart({fil,queryMode: "BDM_SUBMISSIONS_METRICS", type}));
    } 
    else if (roleType === 4) {
      const fil = {addedBy: {
        type: "RECRUITER_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : jobReportTeamNamesIds,
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRecruiterRoleBasedMetricsStart({fil,queryMode: "RECRUITER_METRICS", type}));
    }
  };

  const fetchMetricsOverview = () => {
    const type = activeButton === "myPerformance" ? false : true

    if (roleType === 2) {
      const fil = {addedBy: {
        type: "RA_METRICS_OVERVIEW", 
        value: activeButton === "myPerformance" ?[user?.data?.uiid] : jobReportTeamNamesIds
      }}
      dispatch(getRAMetricsOverviewStart({fil, type}));
    } else if (roleType === 3) {
      const fil = {addedBy: {
        type: "BDM_SUBMISSIONS_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : jobReportTeamNamesIds
      }}
      dispatch(getBDMMetricsOverviewStart({fil,queryMode: "BDM_SUBMISSIONS_METRICS", type}));
    } else if (roleType === 4) {
      const fil = {addedBy: {
        type: "RECRUITER_METRICS", 
        value: activeButton === "myPerformance" ? [user?.data?.uiid] : jobReportTeamNamesIds
      }}
      dispatch(getRecruiterMetricsOverviewStart({fil,queryMode: "RECRUITER_METRICS", type}));
    }
  };

  const fetchMetricGroup = () => {
     const type = activeButton === "myPerformance" ? false : true

    if (roleType === 2) {
      const fil = {addedBy: {
        type: "RA_METRICS_GROUP", 
        value: activeButton === "myPerformance" ?[user?.data?.uiid] : performanceReportTeamNamesIds, 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRAMetricsGroupStart({fil, type}));
    } else if (roleType === 3) {
      const fil = {addedBy: {
        type: "BDM_METRICS_GROUP", 
        value: activeButton === "myPerformance" ?[user?.data?.uiid] : performanceReportTeamNamesIds,
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getBDMMetricsGroupStart({fil, type}));
    } else if (roleType === 4) {
      const fil = {addedBy: {
        type: "RECRUITER_METRICS_GROUP", 
        value: activeButton === "myPerformance" ?[user?.data?.uiid] : performanceReportTeamNamesIds, 
        startDate: finalDataDuration?.from, 
        endDate: finalDataDuration?.to
      }}
      dispatch(getRecruiterMetricsGroupStart({fil, queryMode: "RECRUITER_METRICS_GROUP", type}));
    }
  };

  const getReportToHierarchy = async () => {
    const response = await getDashboardHierarchy({fil: {uiid: {type: "REPORTING_HIERARCHY", value: user?.data?.uiid}}});

    if (response?.success) {
      dispatch(setreportToHierarchy(response?.data?.data?.[0]?.users || {}));
    }
  };  

  const handlePerformanceHierarchyChange = (values) => {
    const ids = values.map((v) => Number(v));
    setPerformanceReportTeamNamesIds(ids);
    setPerformanceReportHierarchy(reportToHierarchy?.filter(u => ids.includes(u.uiid)));
  };

  const hierarchyOptions = useMemo(() => {
    if (!Array.isArray(reportToHierarchy)) return [];
    setPerformanceReportHierarchy(reportToHierarchy || []);

    return reportToHierarchy.map(user => ({
      label: `${formatWords2(user.firstName ?? "")} ${formatWords2(user.lastName ?? "")}`.trim(),
      id: user.uiid
    }));
  }, [reportToHierarchy]);
  const userTimeZoneGroupMetrics = useMemo(() => {
    let metrics;
    let teamMetrics;
    if (roleType === 2) {
      metrics = combineByUserTimezone(metricsGroup, {dateKey: "date", sumKeys: ["accounts", "contacts","confirm","jobOpenings"]});
      teamMetrics = combineByUserTimezone(teamMetricsGroup, {dateKey: "date", sumKeys: ["accounts", "contacts","confirm","jobOpenings"]});
    } else if (roleType === 3) {
      metrics = combineByUserTimezone(metricsGroup, {dateKey: "date", sumKeys: ["positive", "agreementDone","internalSubmission","clientSubmission", "interviewScheduled", "joined", "backout"]});
      teamMetrics = combineByUserTimezone(teamMetricsGroup, {dateKey: "date", sumKeys: ["positive", "agreementDone","internalSubmission","clientSubmission", "interviewScheduled", "joined", "backout"]});
    } else if (roleType === 4) {
      metrics = combineByUserTimezone(metricsGroup, {dateKey: "date", sumKeys: ["activeJobs", "internalSubmissions","clientSubmissions", "interviewScheduled", "closedJobs", "joined"]});
      teamMetrics = combineByUserTimezone(teamMetricsGroup, {dateKey: "date", sumKeys: ["activeJobs", "internalSubmissions","clientSubmissions", "interviewScheduled", "closedJobs", "joined"]});
    }

    return {metrics, teamMetrics}
  }, [metricsGroup, teamMetricsGroup]);

  useEffect(() => {
    if (!finalDataDuration) return;

    fetchMetrics();
    fetchCompareMetrics();
    fetchRoleBasedMetrics();
    fetchMetricGroup();
  }, [finalDataDuration, activeButton]);

  useEffect(() => {
    fetchMetricsOverview();
    getReportToHierarchy();
  }, []);

  useEffect(() => {
    setHasSubordinates(reportToHierarchy?.length > 1)
  }, [reportToHierarchy]);

  useEffect(() => {
    if (!Array.isArray(reportToHierarchy) || reportToHierarchy.length === 0) {
      return;
    }

    const allIds = reportToHierarchy.map(user => user.uiid);

    // Performance Report
    setPerformanceReportTeamNamesIds(prev => {
      if (!prev || prev.length === 0) return allIds;

      const prevSet = new Set(prev);
      const validPrev = allIds.filter(id => prevSet.has(id));

      return validPrev.length ? validPrev : allIds;
    });

    // Job Report
    setJobReportTeamNamesIds(prev => {
      if (!prev || prev.length === 0) return allIds;

      const prevSet = new Set(prev);
      const validPrev = allIds.filter(id => prevSet.has(id));

      return validPrev.length ? validPrev : allIds;
    });

  }, [reportToHierarchy]);

  useEffect(() => {
    if (activeButton === "myTeamPerformance") {
      fetchRoleBasedMetrics();
      fetchMetricsOverview();
    }
  }, [jobReportTeamNamesIds]);

  useEffect(() => {
    if (activeButton === "myPerformance") {
      if (!metricsOverview) {
        fetchMetricsOverview();
      }
    } else if (activeButton === "myTeamPerformance") {
      if (!teamMetricsOverview) {
        fetchMetricsOverview();
      }
    }
  }, [activeButton])

  useEffect(() => {
    setStateLoading(loading);
  }, [loading]);

  return (
    <div className="flex-1 w-full flex flex-col gap-2 overflow-auto">
      {hasSubordinates && (
        <div className="flex justify-between items-center">
          <div>
            <h6>
              My{" "}
              {activeButton === "myPerformance"
                ? "Performance"
                : "Team Performance"}
            </h6>
          </div>
          <div className="flex justify-between items-center">
            <Button
              title={"My Performance"}
              size="sm"
              className="rounded-none"
              onClick={() => setActiveButton("myPerformance")}
              variant={
                activeButton === "myPerformance" ? "contained" : "outlined"
              }
            />
            <Button
              title={"My Team Performance"}
              size="sm"
              className="rounded-none"
              onClick={() => setActiveButton("myTeamPerformance")}
              variant={
                activeButton === "myTeamPerformance" ? "contained" : "outlined"
              }
            />
          </div>
        </div>
      )}
      <InfoDataCardReports
        activeButton={activeButton}
        metrics={metrics?.[0] || {}}
        teamMetrics={teamMetrics?.[0] || {}}
        compareMetrics={compareMetrics?.[0] || {}}
        compareTeamMetrics={compareTeamMetrics?.[0] || {}}
        roleType={roleType}
        loading={stateLoading}
      />

      {hasSubordinates && (
        <div className="flex justify-between items-center mt-5">
          <div>
            <h6>My{" "}
              {activeButton === "myPerformance"
                ? "Performance"
                : "Team Performance"}
            </h6>
          </div>
          <div className="">
            {activeButton === "myTeamPerformance" && (
              <div>
                <div className="flex flex-wrap gap-2 mr-2">
                  <DropdownSelect
                    title="Select Names"
                    name="subordinate"
                    options={hierarchyOptions}
                    values={performanceReportTeamNamesIds}
                    onChange={(values) => handlePerformanceHierarchyChange(values)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div>
        <PerformanceReport
          activeButton={activeButton}
          metrics={userTimeZoneGroupMetrics?.metrics || []}
          teamMetrics={userTimeZoneGroupMetrics?.teamMetrics || []}
          finalDataDuration={finalDataDuration}
          roleType={roleType}
          reportToHierarchy={performanceReportHirerchy}
          loading={stateLoading}
        />

        {hasSubordinates && (
          <div className="flex justify-between items-center mt-5">
            <div>
              <h6>My{" "}
              {activeButton === "myPerformance"
                ? "Performance"
                : "Team Performance"}
              </h6>
            </div>
            <div className="">
              {activeButton === "myTeamPerformance" && (
                <div>
                  <div className="flex flex-wrap gap-2 mr-2">
                    <DropdownSelect
                      title="Select Names"
                      name="subordinate"
                      options={hierarchyOptions}
                      values={jobReportTeamNamesIds}
                      onChange={(values) => {
                        setJobReportTeamNamesIds(values.map((v) => Number(v)));
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2  auto-rows-[350px] ">
          <JobReports
            activeButton={activeButton}
            metrics={roleBasedMetrics?.[0] || {}}
            teamMetrics={teamRoleBasedMetrics?.[0] || []}
            roleType={roleType}
            loading={stateLoading}
          />

          <UpToDateOverview
            activeButton={activeButton}
            metrics={metricsOverview?.[0] || {}}
            teamMetrics={teamMetricsOverview?.[0] || []}
            roleType={roleType}
            loading={stateLoading}
          />
        </div>
      </div>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default OtherUsersDashboard;
